import { useQuery } from 'react-query';
import {
	assignmentWorkplaceUserOptions,
	getEnrichedWorkplaceUsers,
} from 'core/utils/enrichWorkplaceUsers';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';

export interface AssignmentUserOption {
	id: number;
	firstName: string;
	lastName: string;
	email: string;
}

/** Shared with Schedule so the list is fetched once per workplace and reused from cache. */
export const ASSIGNMENT_WORKPLACE_USERS_QUERY_KEY = 'workplace-employees';

const toUserOptions = (data: any[] = []): AssignmentUserOption[] =>
	data
		.map((u: any) => ({
			id: typeof u.id === 'number' ? u.id : Number(u.id),
			firstName: u.firstName ?? u.first_name ?? '',
			lastName: u.lastName ?? u.last_name ?? '',
			email: u.email ?? '',
		}))
		.filter((u) => Number.isFinite(u.id));

/**
 * Workplace users for assignment pickers (Schedule, To-Do, Checklist).
 * Cached per workplace, so opening an add/edit form does not re-fetch users
 * and hierarchy nodes every time.
 */
export const useAssignmentWorkplaceUsers = ({ enabled = true }: { enabled?: boolean } = {}) => {
	const { workplaceId } = useWorkplacesContext();

	return useQuery(
		[ASSIGNMENT_WORKPLACE_USERS_QUERY_KEY, workplaceId],
		() => getEnrichedWorkplaceUsers(workplaceId, assignmentWorkplaceUserOptions),
		{
			enabled: enabled && !!workplaceId,
			staleTime: 300000,
			refetchOnWindowFocus: false,
			select: toUserOptions,
		}
	);
};
